import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Button,
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import {
  IconDots,
  IconEdit,
  IconCopy,
  IconTrash,
  IconPlayerPause,
  IconPlayerPlay,
} from "@tabler/icons-react";
import { SubscriptionStatus } from "@/pages/subscriptions/constants";

interface SubscriptionActionsMenuProps {
  id: string;
  status: SubscriptionStatus;
  readOnly?: boolean;
  onToggleStatus: (id: number, currentStatus: SubscriptionStatus) => void;
  onDelete: (id: number) => void;
  onClone: (id: number) => void;
}

export const SubscriptionActionsMenu = ({
  id,
  status,
  readOnly = false,
  onToggleStatus,
  onDelete,
  onClone,
}: SubscriptionActionsMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  // Check for active status (supports both enum and string literal)
  const isActive = status === SubscriptionStatus.ACTIVE;

  const close = () => setIsOpen(false);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="al-p-2" aria-label="More actions">
          <IconDots size={16} />
        </Button>
      </PopoverTrigger>
      <PopoverContent side="bottom" align="end" className="al-w-max">
        <Stack direction="column" align="start" gap="xxs" className="al-py-1 al-min-w-[100px]">
          <Button
            variant="ghost"
            size="sm"
            className="!al-justify-start al-w-full"
            disabled={readOnly}
            asChild={!readOnly}
          >
            <Link to={`/subscriptions/${id}/edit`} onClick={close}>
              <Stack direction="row" align="center" justify="start" gap="sm">
                <IconEdit size={14} />
                Edit
              </Stack>
            </Link>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="!al-justify-start al-w-full"
            onClick={() => {
              onClone(parseInt(id));
              close();
            }}
          >
            <Stack direction="row" align="center" justify="start" gap="sm">
              <IconCopy size={14} />
              Clone
            </Stack>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="!al-justify-start al-w-full"
            onClick={() => {
              onToggleStatus(parseInt(id), status);
              close();
            }}
          >
            <Stack direction="row" align="center" gap="sm">
              {isActive ? (
                <>
                  <IconPlayerPause size={14} />
                  Deactivate
                </>
              ) : (
                <>
                  <IconPlayerPlay size={14} />
                  Activate
                </>
              )}
            </Stack>
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="!al-justify-start al-text-red-600 hover:al-text-red-700 al-w-full"
              >
                <Stack direction="row" align="center" gap="sm">
                  <IconTrash size={14} />
                  Delete
                </Stack>
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Subscription</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete this subscription? This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="al-bg-red-600 hover:al-bg-red-700"
                  onClick={() => {
                    onDelete(parseInt(id));
                    close();
                  }}
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </Stack>
      </PopoverContent>
    </Popover>
  );
};
